// Scoring system: points for each word length
export const WORD_LENGTH_SCORES = new Map<number, number>([
    [4, 1],   // 4-letter: 1 pt
    [5, 3],   // 5-letter: 3 pts (2 more than previous)
    [6, 6],   // 6-letter: 6 pts (3 more than previous)
    [7, 10],  // 7-letter: 10 pts (4 more than previous)
    [8, 15],  // 8-letter: 15 pts (5 more than previous)
    [9, 21],  // 9-letter: 21 pts (6 more than previous)
    [10, 28], // 10-letter: 28 pts (7 more than previous)
    [11, 36], // 11-letter: 36 pts (8 more than previous)
    [12, 45], // 12-letter: 45 pts (9 more than previous)
]);

// Ranking system: percentage thresholds of realisticMaxScore
export interface RankLevel {
    id: number;
    name: string;
    threshold: number; // percentage (0-100)
}

export const RANK_LEVELS: RankLevel[] = [
    { id: 0, name: 'Novice', threshold: 0 },     // 0%
    { id: 1, name: 'Scribbler', threshold: 10 },  // 10%
    { id: 2, name: 'Explorer', threshold: 25 }, // 25%
    { id: 3, name: 'Decoder', threshold: 40 },   // 40%
    { id: 4, name: 'Scribe', threshold: 60 }, // 60%
    { id: 5, name: 'Wordsmith', threshold: 75 },  // 75%
    { id: 6, name: 'Linguist', threshold: 90 }, // 90%
];

// Special rank name for when user finds all words
export const FOUND_ALL_WORDS_HIDDEN_RANK_NAME = 'Penmaster';

// Bonus types for longest words and anagrams
export type BonusType = 'longest_word' | 'anagram';

// Bonus point values
export const BONUS_POINTS = {
    longest_word: 2,
    anagram: 5,
} as const;

/**
 * Calculate points for a word based on its length
 */
function getPointsForWord(wordLength: number): number {
    return WORD_LENGTH_SCORES.get(wordLength) || 0;
}

/**
 * Calculate bonus points and type for a word
 */
export function getBonusInfo(
    word: string,
    longestSubWordLength: number,
    dailyWordLength: number
): { bonusPoints: number; bonusType: BonusType | undefined } {
    if (word.length !== longestSubWordLength) {
        return { bonusPoints: 0, bonusType: undefined };
    }

    if (word.length === dailyWordLength) {
        // It's an anagram (same length as daily word)
        return { bonusPoints: BONUS_POINTS.anagram, bonusType: 'anagram' };
    } else {
        // It's a longest word (but not an anagram)
        return { bonusPoints: BONUS_POINTS.longest_word, bonusType: 'longest_word' };
    }
}

/**
 * Calculate total points for a word (base points + bonus points)
 */
export function getTotalPointsForWord(
    word: string,
    longestSubWordLength: number,
    dailyWordLength: number
): { totalPoints: number; basePoints: number; bonusPoints: number; bonusType: BonusType | undefined } {
    const basePoints = getPointsForWord(word.length);
    const { bonusPoints, bonusType } = getBonusInfo(word, longestSubWordLength, dailyWordLength);
    const totalPoints = basePoints + bonusPoints;

    return { totalPoints, basePoints, bonusPoints, bonusType };
}

/**
 * Calculate the threshold score for a given rank level
 */
export function getThresholdScore(rankLevel: RankLevel, realisticMaxScore: number): number {
    return Math.ceil((rankLevel.threshold / 100) * realisticMaxScore);
}

/**
 * Determine the current rank based on score and realistic max score
 */
export function getCurrentRank(score: number, realisticMaxScore: number): RankLevel {
    if (realisticMaxScore === 0) {
        return RANK_LEVELS[0];
    }

    // Find the highest rank level the player has achieved based on threshold scores
    let currentRank = RANK_LEVELS[0];
    for (const rankLevel of RANK_LEVELS) {
        const thresholdScore = getThresholdScore(rankLevel, realisticMaxScore);
        if (score >= thresholdScore) {
            currentRank = rankLevel;
        } else {
            break;
        }
    }

    return currentRank;
}

/**
 * Get the next rank and how many points are needed to reach it
 */
export function getNextRankInfo(score: number, realisticMaxScore: number): {
    nextRank: RankLevel | null;
    pointsNeeded: number;
} {
    const currentRank = getCurrentRank(score, realisticMaxScore);
    const nextRankIndex = currentRank.id + 1;

    if (nextRankIndex >= RANK_LEVELS.length) {
        return { nextRank: null, pointsNeeded: 0 };
    }

    const nextRank = RANK_LEVELS[nextRankIndex];
    const pointsForNextRank = getThresholdScore(nextRank, realisticMaxScore);
    const pointsNeeded = Math.max(0, pointsForNextRank - score);

    return { nextRank, pointsNeeded };
}

/**
 * Get progress percentage towards the next rank
 */
export function getProgressToNextRank(score: number, realisticMaxScore: number): number {
    const currentRank = getCurrentRank(score, realisticMaxScore);
    const { nextRank } = getNextRankInfo(score, realisticMaxScore);

    if (!nextRank) {
        return 100; // Already at max rank
    }

    const currentThresholdPoints = getThresholdScore(currentRank, realisticMaxScore);
    const nextThresholdPoints = getThresholdScore(nextRank, realisticMaxScore);
    const pointsInCurrentRange = nextThresholdPoints - currentThresholdPoints;
    const pointsEarnedInCurrentRange = score - currentThresholdPoints;

    if (pointsInCurrentRange === 0) {
        return 100;
    }

    return Math.min(100, Math.max(0, (pointsEarnedInCurrentRange / pointsInCurrentRange) * 100));
}

/**
 * Get overall progress percentage across all ranks (0-100%)
 */
export function getOverallProgress(score: number, realisticMaxScore: number): number {
    if (realisticMaxScore === 0) {
        return 0;
    }

    const currentRank = getCurrentRank(score, realisticMaxScore);
    const maxRank = RANK_LEVELS[RANK_LEVELS.length - 1];

    // If at max rank, return 100%
    if (currentRank.id === maxRank.id) {
        return 100;
    }

    // Calculate position along the rank scale
    // Each rank gets equal space on the progress bar
    const rankSpacing = 100 / (RANK_LEVELS.length - 1);
    const baseProgress = currentRank.id * rankSpacing;

    // Add progress within current rank
    const { nextRank } = getNextRankInfo(score, realisticMaxScore);
    if (nextRank) {
        const currentThresholdPoints = getThresholdScore(currentRank, realisticMaxScore);
        const nextThresholdPoints = getThresholdScore(nextRank, realisticMaxScore);
        const pointsInCurrentRange = nextThresholdPoints - currentThresholdPoints;
        const pointsEarnedInCurrentRange = score - currentThresholdPoints;

        if (pointsInCurrentRange > 0) {
            const progressInCurrentRank = (pointsEarnedInCurrentRange / pointsInCurrentRange) * rankSpacing;
            return Math.min(100, Math.max(0, baseProgress + progressInCurrentRank));
        }
    }

    return baseProgress;
}

/**
 * Calculate total points for a list of words, accounting for bonuses
 */
export function getTotalPointsFromWords(words: string[], dailyWord: string, longestSubWordLength: number): number {
    if (words.length === 0) {
        return 0;
    }

    return words.reduce((total, word) => {
        const { totalPoints } = getTotalPointsForWord(word, longestSubWordLength, dailyWord.length);
        return total + totalPoints;
    }, 0);
}

/**
 * Get rank information from a list of words
 */
export function getRankFromWords(words: string[], realisticMaxScore: number, dailyWord: string, longestSubWordLength: number): { name: string; id: number } {
    const points = getTotalPointsFromWords(words, dailyWord, longestSubWordLength);
    const rank = getCurrentRank(points, realisticMaxScore);
    return { name: rank.name, id: rank.id };
} 